import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  Divider, Paper, Stack, Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { salesInvoicesApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { StatCard } from "@/components/StatCard";
import { StatusPill } from "@/components/StatusPill";
import { formatDateTime } from "@/shared/datetime";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });
const money = (n?: number | null) => (n == null ? "—" : inr.format(n));

/**
 * W17 — how one billed line came to cost what it did.
 *
 * <p><b>Two answers, never mixed.</b> The top half is what was recorded when the bill was raised
 * (W5) — the authoritative one, and the only one that explains the money the patient actually paid.
 * The bottom half is what the same question answers today, which is a different question: rate
 * cards move, charge rows are revised, batches are exhausted. Presenting either as the other is
 * how a support conversation goes wrong.</p>
 *
 * <p>The gap between them is the point of the second half. "Billed at 450, resolves at 500 today"
 * is the start of every conversation about a rate that changed under somebody, and it is invisible
 * unless both questions are asked.</p>
 */
export function LinePriceExplainDialog({
  invoiceId, lineId, onClose,
}: { invoiceId: string; lineId: string | null; onClose: () => void }) {
  const q = useQuery({
    queryKey: ["line-price-explanation", invoiceId, lineId],
    queryFn: () => salesInvoicesApi.explainLinePrice(invoiceId, lineId!),
    enabled: !!lineId,
  });

  const r = q.data;

  return (
    <Dialog open={!!lineId} onClose={onClose} maxWidth="sm" fullWidth>
      <ClosableDialogTitle onClose={onClose}>
        How this line was priced{r ? ` — line ${r.lineNo}` : ""}
      </ClosableDialogTitle>
      <DialogContent dividers>
        {q.isLoading && (
          <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={24} /></Stack>
        )}
        {q.isError && <Alert severity="error">{errorMessage(q.error)}</Alert>}

        {r && (
          <>
            <Typography variant="subtitle2">{r.description}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[r.sku, r.batchNo ? `batch ${r.batchNo}` : null, `${r.qty} × ${money(r.rate)}`]
                .filter(Boolean).join(" · ")}
            </Typography>

            <Stack direction="row" spacing={2} sx={{ my: 2, flexWrap: "wrap" }}>
              <StatCard label="Billed at" value={money(r.listRate ?? r.rate)}
                hint="the rate the server resolved" />
              <StatCard label="Discount" value={money(r.discountAmount)}
                severity={r.discountAmount > 0 ? "warning" : "default"}
                hint={r.discountReason ?? "none"} />
              <StatCard label="Line total" value={money(r.lineTotal)} severity="info"
                hint={`incl. ${money(r.taxAmount)} tax`} />
            </Stack>

            <Typography variant="overline" color="text.secondary">
              What was recorded when the bill was raised
            </Typography>
            <Alert severity={r.priceSource ? "success" : "warning"} sx={{ mt: 1, mb: 2 }}>
              {r.recordedExplanation}
              {r.priceSourceName && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Source: </Typography>
                  <Typography variant="caption"><b>{r.priceSourceName}</b></Typography>
                </Box>
              )}
            </Alert>

            {/* The dimensions are what made the resolver choose as it did — a rate that looks
                wrong is very often a bill raised against the wrong scheme. */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="overline" color="text.secondary">
                What the bill asked for
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
                <Chip size="small" variant="outlined" label={`${r.billType} · ${r.billedOn}`} />
                {r.customerName && <Chip size="small" variant="outlined" label={r.customerName} />}
                {r.insuranceSchemeName
                  ? <StatusPill label={r.insuranceSchemeName} tone="info" />
                  : <Chip size="small" variant="outlined" label="No scheme" />}
                {r.personTypeName && <StatusPill label={r.personTypeName} tone="neutral" />}
                {r.admissionTypeName && <StatusPill label={r.admissionTypeName} tone="neutral" />}
              </Stack>
            </Paper>

            {r.discountReason && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <b>Discounted:</b> {r.discountReason}
                {r.discountApprovedByName && (
                  <Box sx={{ mt: 0.5 }}>
                    Approved by {r.discountApprovedByName}
                    {r.discountApprovedAt ? ` · ${formatDateTime(r.discountApprovedAt)}` : ""}
                  </Box>
                )}
              </Alert>
            )}

            <Divider sx={{ mb: 2 }} />
            <Typography variant="overline" color="text.secondary">
              What the same question answers today
            </Typography>

            {r.todayProblem ? (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {r.todayProblem}
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="caption">
                    This does not affect the bill above — it was priced when it was raised, and that
                    decision is recorded.
                  </Typography>
                </Box>
              </Alert>
            ) : r.differs ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                <b>The rate has moved.</b> This line was billed at {money(r.listRate)}; the same
                item, for the same patient, resolves at <b>{money(r.todayRate)}</b> today
                {r.todaySource ? ` (${r.todaySource})` : ""}. The bill is not wrong — the rate card
                behind it changed after it was raised.
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mt: 1 }}>
                Unchanged — the same query today gives {money(r.todayRate)}.
                {r.todayExplanation ? ` ${r.todayExplanation}` : ""}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
